import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { EntriesProvider } from "@/context/EntriesContext";
import { SharesProvider } from "@/context/SharesContext";

import Login from "@/pages/Login";
import Activate from "@/pages/Activate";
import Studio from "@/pages/Studio";
import Sessions from "@/pages/Sessions";
import SessionPage from "@/pages/SessionPage";
import Milestones from "@/pages/Milestones";
import Measures from "@/pages/Measures";
import Deliverables from "@/pages/Deliverables";
import Intake from "@/pages/Intake";
import ActivityTool from "@/pages/ActivityTool";
import ExportKit from "@/pages/ExportKit";
import Messages from "@/pages/Messages";
import CoachDashboard from "@/pages/CoachDashboard";
import SessionPack from "@/pages/SessionPack";
import CoachParticipant from "@/pages/CoachParticipant";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-linen">
      <div className="font-serif text-2xl text-stone-400 ai-thinking">Building The Bridge…</div>
    </div>
  );
}

function Protected({ children, role }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "coach" ? "/coach" : "/studio"} replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={user.role === "coach" ? "/coach" : "/studio"} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/activate" element={<Activate />} />

      {/* Participant */}
      <Route path="/studio" element={<Protected role="participant"><Studio /></Protected>} />
      <Route path="/sessions" element={<Protected role="participant"><Sessions /></Protected>} />
      <Route path="/sessions/:id" element={<Protected role="participant"><SessionPage /></Protected>} />
      <Route path="/milestones" element={<Protected role="participant"><Milestones /></Protected>} />
      <Route path="/measures" element={<Protected role="participant"><Measures /></Protected>} />
      <Route path="/deliverables" element={<Protected role="participant"><Deliverables /></Protected>} />
      <Route path="/intake" element={<Protected role="participant"><Intake /></Protected>} />
      <Route path="/tools/:toolId" element={<Protected role="participant"><ActivityTool /></Protected>} />
      <Route path="/messages" element={<Protected role="participant"><Messages /></Protected>} />
      <Route path="/export" element={<Protected><ExportKit /></Protected>} />

      {/* Coach */}
      <Route path="/coach" element={<Protected role="coach"><CoachDashboard /></Protected>} />
      <Route path="/coach/participant" element={<Protected role="coach"><CoachParticipant /></Protected>} />
      <Route path="/coach/pack/:packId" element={<Protected role="coach"><SessionPack /></Protected>} />

      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <EntriesProvider>
            <SharesProvider>
              <AppRoutes />
            </SharesProvider>
          </EntriesProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
